// src/components/BookingItem.js

import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Avatar, Chip, ListItemButton, ListItemText, Divider } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';

// Styled components
const StyledListItem = styled(ListItemButton)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
}));

const ListingImage = styled('img')({
    width: '120px',
    height: '120px',
    objectFit: 'cover',
    borderRadius: '8px',
    marginRight: '16px',
});

const UserAvatar = styled(Avatar)({
    width: '32px',
    height: '32px',
    fontSize: '14px',
    marginRight: '8px',
});

// Function to get user initials
const getUserInitials = (name) => {
    if (!name) return '';
    const nameParts = name.split(' ');
    return `${nameParts[0][0].toUpperCase()}${nameParts[1] ? nameParts[1][0].toUpperCase() + '.' : ''}`.trim();
};

// Function to get status color
const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
        case 'pending':
            return 'warning';
        case 'accepted':
        case 'paid':
        case 'in_progress':
        case 'booked':
        case 'picked up':
            return 'success';
        case 'canceled':
        case 'declined':
        case 'cancelled':
        case 'expired':
        case 'failed':
            return 'error';
        default:
            return 'default';
    }
};

const BookingItem = ({ booking, role }) => {
    // role: 'borrower' or 'lender'
    // role determines which user information to display
    const isBorrower = role === 'borrower';
    const userName = isBorrower ? booking.listing_owner_name : booking.borrower_name;
    const userProfilePicture = isBorrower ? booking.listing_owner_profile_picture : booking.borrower_profile_picture;
    const bookingLink = isBorrower ? `/transactions/${booking.id}` : `/lending-bookings/${booking.id}`;

    return (
        <>
            <StyledListItem component={RouterLink} to={bookingLink}>
                <ListingImage src={booking.listing_image} alt={booking.listing_title} />
                <Box sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                        <UserAvatar src={userProfilePicture} alt={userName}>
                            {userName ? getUserInitials(userName) : 'U'}
                        </UserAvatar>
                        <Typography variant="subtitle1" component="span">
                            {getUserInitials(userName)}
                        </Typography>
                        <Chip
                            label={booking.status}
                            color={getStatusColor(booking.status)}
                            size="small"
                            sx={{ ml: 'auto' }}
                        />
                    </Box>
                    <ListItemText
                        primary={booking.listing_title || 'Unnamed Item'}
                        secondary={
                            <>
                                <Typography component="span" variant="body2" color="text.primary">
                                    {isBorrower
                                        ? booking.status === 'pending'
                                            ? 'Your request has been sent'
                                            : `You are chatting with ${getUserInitials(userName)}`
                                        : booking.status === 'pending'
                                            ? 'New booking request'
                                            : `Booking ${booking.status.toLowerCase()}`}
                                </Typography>
                                <br />
                                {`Rental period: ${new Date(booking.start_date).toLocaleDateString()} - ${new Date(booking.end_date).toLocaleDateString()}`}
                                <br />
                                {`Last activity: ${new Date(booking.updated_at).toLocaleDateString()}`}
                                <br />
                                {`Amount: $${booking.total_transaction_amount.toFixed(2)}`}
                            </>
                        }
                    />
                </Box>
                <ChevronRightIcon color="action" sx={{ alignSelf: 'center' }} />
            </StyledListItem>
            <Divider variant="inset" component="li" />
        </>
    );
};

BookingItem.propTypes = {
    booking: PropTypes.object.isRequired,
    role: PropTypes.oneOf(['borrower', 'lender']).isRequired,
};

export default BookingItem;
